<template>
  <div
    class="relative btn-cart-wrap"
    :class="{wide: wide}"
  >
    <button
      type="button"
      class="add-to-cart btn btn-success rounded-pill font-medium text-nowrap w-100 relative zindex-2"
      :class="[padding, { 'in-cart events-none': quantityInCart > 0 }]"
      aria-label="Add to cart"
      @click.prevent="addToCart"
    >
      <i
        class="mi-cart icon-20"
        :class="{ 'mr-3 sm:mr-2': !iconButtonOnly }"
      />
      <template v-if="!iconButtonOnly">
        {{ t('to_cart') }}
      </template>
    </button>

    <div
      v-if="quantityInCart > 0"
      class="increase-in-cart text-center absolute flex right-0 top-0 rounded-pill"
      :class="[`bg-${theme}`, { 'w-100': !iconButtonOnly }]"
    >
      <ButtonMinus
        :icon-size="width"
        class="icon btn btn-muffled rounded-pill border-0"
        :class="{ 'mr-1': iconButtonOnly }"
        @click="decreaseQuantity"
      />
      <span
        v-if="!iconButtonOnly"
        class="flex-fill rounded-pill bg-white border mx-1 overflow-hidden flex justify-center items-center"
        :class="{ 'my-1': !odometerFill }"
      >
        <BaseOdometer :number="quantityInCart" />
      </span>
      <ButtonPlus
        class="relative"
        :icon-size="width"
        :counter="quantityInCart && iconButtonOnly ? quantityInCart : 0"
        @click="incrementQuantity"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { storeToRefs } from 'pinia'
import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import BaseOdometer from '@/components/general/BaseOdometer.vue'
import ButtonMinus from '@/components/buttons/ButtonMinus.vue'
import ButtonPlus from '@/components/buttons/ButtonPlus.vue'
import { GtmEventEmitter } from '@/utils/gtmEventEmitter'
import { CartService } from '@/services/cartService'
import { useAppStore } from '@/stores/AppStore'
import { useCartStore } from '@/stores/CartStore'
import { useModalStore } from '@/stores/ModalStore'
import { isAxiosError } from 'axios'

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },

  gtmSource: {
    type: String,
    default: undefined,
  },

  theme: {
    type: String,
    default: 'primary-200',
  },

  width: {
    type: String,
    default: '18',
  },

  iconButtonOnly: {
    type: Boolean,
    default: false,
  },

  odometerFill: {
    type: Boolean,
    default: false,
  },

  wide: {
    type: Boolean,
    default: false,
  },

  padding: {
    type: String,
    default: 'p-3 sm:p-2'
  }
})
const { t } = useI18n()

const { openModal } = useModalStore()
const appStore = useAppStore()
const cartStore = useCartStore()

const { getCurrentCustomer } = storeToRefs(appStore)

const emit = defineEmits(['cartOperation'])
const currentTime = new Date().toLocaleTimeString('cs-CZ', { second: 'numeric' });

const quantityInCart = computed(() => cartStore.getProductQuantity(props.product.id))

const showDiscountModal = () => {
  return (
    props.product.quantityDiscounts &&
    (!props.product.plusClubPrice || !getCurrentCustomer.value?.inPlusClub) &&
    props.product.state === 'available'
  )
}

const addToCart = async () => {
  if (showDiscountModal()) {
    openModal({
      id: 'modal-quantity-discount-table',
      params: {
        product: props.product,
      },
    })
  }
  try {
    const cartService = new CartService()
    const { data } = await cartService.addProduct(props.product.id, 1)
    const {
      data: { product },
    } = await ApiServiceV1.get(`products/${props.product.permalink}`)
    GtmEventEmitter.addToCart({
      product: product,
      quantity: 1,
      isFavorite: appStore.isFavorite(product.id),
      source: props.gtmSource,
    })
    incrementCallback(data)
  } catch (error) {
    if (isAxiosError(error)) {
      openModal({
        id: 'modal-alert',
        title: error.response!.data.msg,
      })
    }
  }
}

const incrementQuantity = async () => {
  if (showDiscountModal()) {
    openModal({
      id: 'modal-quantity-discount-table',
      params: {
        product: props.product,
      },
    })
  }
  try {
    const cartService = new CartService()
    const { data } = await cartService.changeProductQuantity(props.product.id, 1)
    const {
      data: { product },
    } = await ApiServiceV1.get(`products/${props.product.permalink}`)
    GtmEventEmitter.addToCart({
      product: product,
      quantity: 1,
      isFavorite: appStore.isFavorite(product.id),
      source: props.gtmSource,
    })
    incrementCallback(data)
  } catch (error) {
    if (isAxiosError(error)) {
      openModal({
        id: 'modal-alert',
        title: error.response!.data.msg,
      })
    }
  }
}

const decreaseQuantity = async () => {
  try {
    const cartService = new CartService()
    const cartItem = cartStore.getProductInCart(props.product.id)!
    if (quantityInCart.value > 1) {
      const { data } = await cartService.changeProductQuantity(props.product.id, -1)
      const {
        data: { product },
      } = await ApiServiceV1.get(`products/${props.product.permalink}`)
      GtmEventEmitter.removeFromCart({
        product: product,
        quantity: 1,
        isFavorite: appStore.isFavorite(product.id),
        source: props.gtmSource,
      })
      decreaseCallback(data)
    } else {
      openModal({
        id: 'modal-remove-item',
        params: {
          itemId: cartItem.id,
          name: cartItem.name,
          noticeText: t('product_quantity_removed', { name: props.product.name }),
          removeCallback: async (itemId: string) => {
            await cartService.deleteProduct(itemId)
            const {
              data: { product },
            } = await ApiServiceV1.get(`products/${props.product.permalink}`)
            GtmEventEmitter.removeFromCart({
              product: product,
              quantity: 1,
              isFavorite: appStore.isFavorite(props.product.id),
              source: props.gtmSource,
            })
            emit('cartOperation', { action: 'delete', timeRecord: currentTime } )
          },
        },
      })
    }
  } catch (error) {
    if (isAxiosError(error)) {
      openModal({
        id: 'modal-alert',
        title: error.response!.data.msg,
      })
    }
  }
}

const incrementCallback = (data: unknown) => {
  emit('cartOperation', { action: 'increment', timeRecord: currentTime })
}

const decreaseCallback = (data: unknown) => {
  emit('cartOperation', { action: 'decrement', timeRecord: currentTime } )
}
</script>

<style lang="scss">
.btn-cart-wrap {
  &.wide {
    min-width: 170px;
  }
  .add-to-cart {
    transition: .5s transform, .5s opacity;
    transform: translateY(0%);
    opacity: 1;

    &.in-cart {
      opacity: 0;
      transform: translateX(10%);
    }
  }
  .increase-in-cart {
    .btn {
      padding: 12px;
    }
  }
}
</style>

<i18n>
  {
    "cs": {
    "product_quantity_decremented": "{name} jste odebrali z košíku",
    "product_quantity_incremented": "{name} – hop a je to v košíku!",
    "product_quantity_removed": "{name} už není v košíku.",
    "to_cart": "Do košíku"
},
    "sk": {
    "product_quantity_decremented": "{name} ste odstránili z košíka",
    "product_quantity_incremented": "{name}- hop a je to v košíku!",
    "product_quantity_removed": "{name} už nie je v košíku.",
    "to_cart": "Do košíka"
},
    "pl": {
    "product_quantity_decremented": "{name} został usunięty z koszyka",
    "product_quantity_incremented": "{name}– hop i jest w koszyku!",
    "product_quantity_removed": "{name} nie znajduje się już w koszyku.",
    "to_cart": "Do koszyka"
},
    "hu": {
    "product_quantity_decremented": "{name} kivettél a kosárból",
    "product_quantity_incremented": "{name} - hop és máris a kosárban van!",
    "product_quantity_removed": "{name} már nincs a kosárban.",
    "to_cart": "Kosárba"
},
    "de": {
    "product_quantity_decremented": "{name} wurde aus dem Warenkorb entfernt. ",
    "product_quantity_incremented": "{name} - hurra, dein Artikel ist im Warenkorb!",
    "product_quantity_removed": "{name} befindet sich nicht mehr im Warenkorb.",
    "to_cart": "Hinzufügen"
},
    "at": {
    "product_quantity_decremented": "{name} wurde aus dem Warenkorb entfernt. ",
    "product_quantity_incremented": "{name} - hurra, dein Artikel ist im Warenkorb!",
    "product_quantity_removed": "{name} befindet sich nicht mehr im Warenkorb.",
    "to_cart": "Hinzufügen"
},
    "ro": {
    "product_quantity_decremented": "{name}ați eliminat din coș",
    "product_quantity_incremented": "{name} – hop și este în coș!",
    "product_quantity_removed": "{name} nu mai este în coș.",
    "to_cart": "Adăugați în coș"
},
    "fr": {
    "product_quantity_decremented": "{name} vous avez retiré du panier",
    "product_quantity_incremented": "{name}- hop et c'est dans le panier !",
    "product_quantity_removed": "{name}n'est plus dans le panier.",
    "to_cart": "Au panier"
}
  }
</i18n>
